import { parseDate } from "@progress/kendo-intl";
import { FormRenderProps } from "@progress/kendo-react-form";
import { useInternationalization } from "@progress/kendo-react-intl";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { InsuranceSubmitSummary, RetentionCard } from ".";
import { QuoteExpiry, SubmitDisclaimer } from "..";
import documentsApi from "../../../api/documents";
import insuranceApi from "../../../api/insurance";
import quotes from "../../../api/quotes";
import { useApi } from "../../../hooks/useApi";
import { useUserContext } from "../../../hooks/useUserContext";
import DateUtility from "../../../utilities/dateUtilities";
import { sumByProperty } from "../../../utilities/objectUtilities";
import { LoadingIndicator } from "../../LoadingIndicator";
import PdfDownloadButton from "../quoteDetails/PdfDownloadButton";
import InsuranceUtility from "./insuranceUtilities";

interface IInsuranceSubmitProps {
  formRenderProps: FormRenderProps;
  product: any;
  disableSubmit?: boolean;
}

export const Submit = ({ formRenderProps, product, disableSubmit }: IInsuranceSubmitProps) => {
  const { context: user } = useUserContext();
  const { commodity, company } = product;

  const formatter = useInternationalization();
  const getQuoteApi = useApi(insuranceApi.getQuote);

  const startDate = parseDate(formRenderProps.valueGetter("contract.start"));
  const endDate = parseDate(formRenderProps.valueGetter("contract.end"));
  const retentionLevels = formRenderProps.valueGetter("retentionLevels");
  const selectedLevel = formRenderProps.valueGetter("selectedLevel");
  const level = retentionLevels[selectedLevel];
  const quote = level.quote;
  const totalPrice = sumByProperty(quote, "brokerPrice");
  const coverages = InsuranceUtility.getCoverageData(formRenderProps);
  const hasSubmitPermission =
    user.permissions.canSubmitQuotes && user.roles.canSubmitQuotes(company.role);
  const hasCredit = company.features?.hasCredit;
  const canSubmit = hasSubmitPermission && hasCredit;

  const quoteId = formRenderProps.valueGetter("quoteId");
  const pricingId = formRenderProps.valueGetter("pricingId");
  const getDetails = useQuery({
    queryKey: ["quoteDetails", quoteId, pricingId],
    queryFn: () => quotes.getDetails(quoteId),
  });

  const quoteSummary = {
    loading: false,
    units: commodity.units.slice(0, -1),
    coverages: coverages,
    prices: level.pricing.details,
    quote: getDetails.data?.quote.transactionDetails || [],
    companyRole: company.role,
    unitPriceDecimals: commodity.decimals,
  };

  const quoteDetails = useMemo(() => {
    return {
      units: commodity.units,
      coverage: formatter.formatNumber(formRenderProps.valueGetter("totalCoverage"), "c0"),
      volume: formRenderProps.valueGetter("volume"),
      startDate: DateUtility.formatDateMY(startDate),
      endDate: DateUtility.formatDateMY(endDate),
      retentionLevel: formatter.formatNumber(level.value, "p0"),
      quoteId,
      pricingId,
      name: formRenderProps.valueGetter("quoteName"),
      price: formatter.formatNumber(totalPrice, "c2"),
    };
  }, [commodity.units, endDate, formRenderProps, formatter, level.value, startDate, totalPrice]);

  const [loading, setLoading] = useState(false);
  const [expiration, setExpiration] = useState(
    new Date(Date.parse(formRenderProps.valueGetter("expiry"))),
  );
  const checkboxFieldName = "isConfirmed";

  const getQuote = useCallback(async () => {
    getQuoteApi.request(quoteDetails.quoteId);
  }, [getQuoteApi, quoteDetails]);

  useEffect(() => {
    setLoading(getQuoteApi.loading);

    if (getQuoteApi.data) setExpiration(new Date(Date.parse(getQuoteApi.data.quoteExpiry)));
  }, [getQuoteApi.data, getQuoteApi.loading]);

  const onExpiryChanged = (expiry: string) => {
    formRenderProps.onChange("expiry", { value: expiry });
  };

  const handleAgree = () => {
    formRenderProps.onChange(checkboxFieldName, { value: true });
  };

  return (
    <>
      <LoadingIndicator loading={loading || !!disableSubmit} />
      <div style={{ display: "flex", justifyContent: "center" }}>
        <div style={{ flexBasis: "90%" }}>
          <RetentionCard
            name={selectedLevel}
            fieldName={`retentionLevels.${selectedLevel}.value`}
            isReadOnly={true}
            title={"Quote Summary"}
            quoteSummary={quoteSummary}
            isSelected={true}
          />

          <PdfDownloadButton
            quoteId={quoteDetails.quoteId}
            pricingId={quoteDetails.pricingId}
            api={documentsApi.getQuoteSummaryPdf}
          />

          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              marginTop: "1rem",
            }}
          >
            <div style={{ flexBasis: "50%" }}>
              <SubmitDisclaimer
                canSubmit={canSubmit}
                hasCredit={hasCredit}
                fieldName={checkboxFieldName}
                handleAgree={handleAgree}
              >
                <InsuranceSubmitSummary quoteDetails={quoteDetails} canSubmit={canSubmit} />
              </SubmitDisclaimer>
            </div>

            <QuoteExpiry
              expiration={expiration}
              hideExpiry={true}
              loading={loading}
              onExpire={onExpiryChanged}
              onRefresh={getQuote}
            />
          </div>
        </div>
      </div>
    </>
  );
};
